import { Controller } from "@hotwired/stimulus"
import { afterOnboarding } from "../lib/onboarding"
import { afterMapInteraction, CONSENT_DONE_EVENT } from "../lib/prompt_sequence"
import { lockScroll, unlockScroll } from "../lib/scroll_lock"

/**
 * Consent Prompt Controller — contextual L3 opt-in nudge.
 *
 * Shown on EVERY /home visit for users who have NOT opted in (the component's render? already excludes
 * opted-in users, so once they tap 開啟 it stops). Slides up after the first map interaction, so the map is
 * seen first; the notification prompt waits until this sheet is answered (lib/prompt_sequence). NEVER auto-grants:
 * "開啟" posts the explicit opt-in; "稍後再說" closes for this visit (a "later", not a hard decline — so we
 * re-ask on the next visit; no cooldown). No default-on, no pre-tick, no penalty for declining.
 */
const SETTLE_MS = 1200 // after the first pan/zoom, so the sheet never lands mid-gesture
const ANIMATION_MS = 300

export default class extends Controller {
  static values = { consentUrl: String }

  connect() {
    this.cancelWait = afterOnboarding(() => {
      this.cancelInteraction = afterMapInteraction(() => { this.timer = setTimeout(() => this.open(), SETTLE_MS) })
    })
  }

  disconnect() {
    this.cancelWait?.()
    this.cancelInteraction?.()
    clearTimeout(this.timer)
    unlockScroll(this)
  }

  open() {
    this.element.classList.remove("hidden")
    lockScroll(this)
    requestAnimationFrame(() => { this.element.dataset.state = "open" })
  }

  close() {
    this.element.dataset.state = "closed"
    this.element.dataset.answered = "true"
    unlockScroll(this)
    setTimeout(() => {
      this.element.classList.add("hidden")
      window.dispatchEvent(new CustomEvent(CONSENT_DONE_EVENT))
    }, ANIMATION_MS)
  }

  // 開啟 — explicit opt-in to L3 behavioral collection. Model B: aggregate licensing is notice + opt-out in
  // Settings, never a consent toggle here.
  async enable(event) {
    event.preventDefault()
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.consentUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ l3: "1" })
      })
      if (res.ok) this.close()
    } catch (_e) {
      // network error — leave the sheet open so the user can retry; never silently "consent"
    }
  }

  // 稍後再說 — closes for THIS visit only; re-asks next /home visit (no cooldown).
  dismiss(event) {
    event?.preventDefault()
    this.close()
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.dismiss(event)
  }
}
